import "./DatePicker.scss";

import * as Dfns from "date-fns/fp";
import * as R from "ramda";

import { Button, ButtonType } from "../Button";
import {
  CALENDAR_HEIGHT,
  CALENDAR_SPACE,
  CALENDAR_WIDTH,
  COMP_CALENDAR_HEIGHT,
  DATE_INTERVALS,
  DEFAULT_IS_DAYBLOCKED,
  DEFAULT_MAX_YEAR,
  DEFAULT_MIN_YEAR,
  FOCUS_OPTIONS,
  GRAPH_INTERVALS_WIDTH,
  PRESETS_WIDTH,
} from "./DatePickerButton/DatePickerConstants";
import {
  COMP_PER_TYPES,
  CustomPresetState,
  DATE_PRESETS,
  INIT_CUSTOM_PRESET_STATE,
  RangeErrorPreset,
  computeDateFromComparisonPreset,
  computeDateFromPreset,
  defaultIsOutsideRange,
} from "@blisspointmedia/bpm-types/dist/RelativeDatePresets";
import {
  CompanyFiscalCal,
  isOutsideFiscalYearRange,
} from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import { DateRange, Setter } from "../../utils/types";
import { Mixpanel, MxE } from "../../utils/mixpanelWrapper";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DPCalendar } from "./DPCalendar";
import { DPPresets } from "./DPPresets";
import DateInputField from "./DateInputField/DateInputField";
import { DatePickerButton } from "./DatePickerButton";
import { FaWrench } from "react-icons/fa";
import { MdArrowForwardIos } from "react-icons/md";
import { OverlayTrigger } from "../OverlayTrigger";
import ReactDOM from "react-dom";
import { ToggleSwitch } from "../ToggleSwitch";
import { Tooltip } from "react-bootstrap";
import cn from "classnames";
import { useCompanyInfo } from "../../redux/company";
import { useRenderedPosition } from "../../utils/hooks/useDOMHelpers";

interface SingleDatePickerProps {
  range?: DateRange;
  onChange: (newDates: DateRange) => void;
  minYear?: number;
  maxYear?: number;
  isOutsideRange?: (date: string) => boolean;
  isDayBlocked?: (date: string) => boolean;
  bordered?: boolean;
  currentMonthSecond?: boolean;
  fullWeeksOnly?: boolean; // Used for linear pages in which we only allow selection of entire weeks starting on Mondays.
  maxDate?: string; // Used for brand health page to explain why certain dates are greyed out.
  dateInterval?: boolean;
  dateIntervalOnChage?: (param: string) => void;
  datePresets?: boolean;
  removeHeaderText?: boolean;
  fiscalCalOverride?: CompanyFiscalCal; // pass in an empty object for a standard calendar
  design?: "primary" | "secondary";
  light?: boolean;
  type?: ButtonType;
  disabled?: boolean;
  additionalRightHeaderItems?: React.ReactNode;
  onApply?: () => void;
}

interface ComparisonDatePickerProps extends SingleDatePickerProps {
  comparison: true;
  comparisonRange: DateRange | undefined;
  comparisonOnChange: (newDates: DateRange) => void;
  disableComparisonSetter?: Setter<boolean>;
}

type DatePickerProps =
  | (SingleDatePickerProps & {
      comparisonRange?: never;
      comparisonOnChange?: never;
      comparison?: never;
      disableComparisonSetter?: never;
    })
  | ComparisonDatePickerProps;

export const DatePicker: React.FC<DatePickerProps> = ({
  range,
  onChange,
  minYear = DEFAULT_MIN_YEAR,
  maxYear = DEFAULT_MAX_YEAR,
  isOutsideRange = defaultIsOutsideRange,
  isDayBlocked = DEFAULT_IS_DAYBLOCKED,
  currentMonthSecond,
  fullWeeksOnly,
  maxDate,
  dateInterval = false,
  datePresets = false,
  comparison = false,
  comparisonRange,
  comparisonOnChange,
  dateIntervalOnChage,
  disableComparisonSetter,
  removeHeaderText = false,
  fiscalCalOverride,
  design = "primary",
  light,
  type,
  disabled = false,
  additionalRightHeaderItems,
  onApply,
}) => {
  // Years for the year picker
  const years = useMemo(() => R.range(minYear, maxYear + 1), [minYear, maxYear]);
  const { start, end } = range || {};
  const { start: comparisonStart, end: comparisonEnd } = comparisonRange || {};
  const ref = useRef<HTMLDivElement>(null);
  const [position, resetPosition] = useRenderedPosition(ref);
  const [show, setShowRaw] = useState(false);
  const { fiscal_cal, cid } = useCompanyInfo();
  const fiscalCal = useMemo(() => (fiscalCalOverride ? fiscalCalOverride : fiscal_cal), [
    fiscalCalOverride,
    fiscal_cal,
  ]);
  const [dateInputs, setDateInputs] = useState<Partial<DateRange>>({
    start,
    end,
  });
  const [comparisonDateInputs, setComparisonDateInputs] = useState<Partial<DateRange>>({
    start: comparisonStart,
    end: comparisonEnd,
  });
  const [focus, setFocus] = useState<typeof FOCUS_OPTIONS[number]>("START");
  const [comparisonFocus, setComparisonFocus] = useState<typeof FOCUS_OPTIONS[number]>("START");
  const [selectedInterval, setSelectedInterval] = useState<typeof DATE_INTERVALS[number] | null>();
  const [selectedPreset, setSelectedPreset] = useState<typeof DATE_PRESETS[number][number] | null>(
    null
  );
  const [selectedCompPerType, setSelectedCompPerType] = useState<
    typeof COMP_PER_TYPES[number][number] | null
  >(null);
  const [comparisonEnabled, setComparisonEnabled] = useState<boolean>(true);
  const [startCustomPreset, setStartCustomPreset] = useState<CustomPresetState>({
    ...INIT_CUSTOM_PRESET_STATE,
  });
  const [endCustomPreset, setEndCustomPreset] = useState<CustomPresetState>({
    ...INIT_CUSTOM_PRESET_STATE,
  });
  const [startCompCustomPreset, setStartCompCustomPreset] = useState<CustomPresetState>({
    ...INIT_CUSTOM_PRESET_STATE,
  });
  const [endCompCustomPreset, setEndCompCustomPreset] = useState<CustomPresetState>({
    ...INIT_CUSTOM_PRESET_STATE,
  });
  const [compOpen, setCompOpen] = useState<boolean>(false);
  const [rangeErrorPreset, setRangeErrorPreset] = useState<RangeErrorPreset>(null);
  const [compRangeErrorPreset, setCompRangeErrorPreset] = useState<RangeErrorPreset>(null);
  const [firstUserChange, setFirstUserChange] = useState<[number, number]>([0, 0]);

  const resolvedPosition = useMemo(() => {
    // Start off anchored below the input aligned on the right
    const combinedWidth =
      CALENDAR_WIDTH + (datePresets ? PRESETS_WIDTH : dateInterval ? GRAPH_INTERVALS_WIDTH : 0);
    const combinedHeight = comparison ? COMP_CALENDAR_HEIGHT : CALENDAR_HEIGHT;
    let top = position.bottom + CALENDAR_SPACE;
    let left = position.right - combinedWidth;
    const overlayStyle: { height?: string; width?: string } = {};
    // If it spills off the bottom of the page, open up
    if (top + combinedHeight > window.innerHeight) {
      top = CALENDAR_SPACE;
      if (top + combinedHeight > window.innerHeight) {
        overlayStyle.height = `${top + combinedHeight + CALENDAR_SPACE}px`;
      }
    }
    // search for a better spot
    if (left < 0) {
      ({ left } = position);
      if (left + combinedWidth > window.innerWidth) {
        left = window.innerWidth / 2 - combinedWidth / 2;
        if (left + combinedWidth > window.innerWidth) {
          left = CALENDAR_SPACE;
          if (left + combinedWidth > window.innerWidth) {
            overlayStyle.width = `${left + combinedWidth + CALENDAR_SPACE}px`;
          }
        }
      }
    }
    return { position: { top, left }, overlay: overlayStyle };
  }, [position, dateInterval, datePresets, comparison]);

  const resolvedIsOutsideValidRange = useCallback(
    (date: string) => {
      const invalidFormat = !Dfns.isValid(Dfns.parseISO(date));
      if (invalidFormat) {
        return true;
      }

      const outsideYearRange = isOutsideFiscalYearRange(
        date,
        years[0],
        years[years.length - 1],
        fiscalCal
      );
      return isOutsideRange(date) || isDayBlocked(date) || outsideYearRange || invalidFormat;
    },
    [isOutsideRange, isDayBlocked, years, fiscalCal]
  );
  // okay need to add an override of fiscal cal
  const resetState = useCallback(() => {
    setSelectedInterval(null);
    setSelectedPreset(null);
    setSelectedCompPerType(null);
    setStartCustomPreset({ ...INIT_CUSTOM_PRESET_STATE });
    setEndCustomPreset({ ...INIT_CUSTOM_PRESET_STATE });
    setStartCompCustomPreset({ ...INIT_CUSTOM_PRESET_STATE });
    setEndCompCustomPreset({ ...INIT_CUSTOM_PRESET_STATE });
    setCompOpen(false);
    setRangeErrorPreset(null);
    setCompRangeErrorPreset(null);
  }, []);

  const setShow = useCallback(
    (show: boolean, save?: boolean) => {
      resetPosition();
      setShowRaw(show);
      if (!show && dateInputs.start && dateInputs.end) {
        setFocus("START");
        setDateInputs({ start, end });
        if (comparison) {
          setComparisonDateInputs({
            start: comparisonStart,
            end: comparisonEnd,
          });
          if (disableComparisonSetter) {
            disableComparisonSetter(!comparisonEnabled);
          }
          setComparisonEnabled(true);
        }
        let mixpanelLogPrimary = false;
        let mixpanelLogComparison = false;

        if (save && (dateInputs.start !== start || dateInputs.end !== end)) {
          onChange(dateInputs as DateRange);
          mixpanelLogPrimary = true;
        }

        if (
          comparison &&
          save &&
          comparisonOnChange &&
          comparisonDateInputs &&
          (comparisonDateInputs.start !== comparisonStart ||
            comparisonDateInputs.end !== comparisonEnd)
        ) {
          comparisonOnChange(comparisonDateInputs as DateRange);
          mixpanelLogComparison = true;
        }

        if (mixpanelLogPrimary && mixpanelLogComparison) {
          Mixpanel.track(MxE.DATE_CHANGE, { primary_or_comparison: "both" });
        } else if (mixpanelLogPrimary) {
          Mixpanel.track(MxE.DATE_CHANGE, { primary_or_comparison: "primary" });
        } else if (mixpanelLogComparison) {
          Mixpanel.track(MxE.DATE_CHANGE, { primary_or_comparison: "comparison" });
        }
      } else if (show && disableComparisonSetter) {
        disableComparisonSetter(false);
      }
      if (!show) {
        resetState();
      } else {
        setFirstUserChange([0, 0]);
      }
    },
    [
      onChange,
      dateInputs,
      resetPosition,
      start,
      end,
      comparison,
      comparisonDateInputs,
      comparisonEnd,
      comparisonStart,
      comparisonOnChange,
      comparisonEnabled,
      disableComparisonSetter,
      resetState,
    ]
  );

  useEffect(() => {
    setComparisonDateInputs(dateInputs => {
      let newDateInputs = { ...dateInputs };
      let changed = false;
      if (dateInputs.start !== comparisonStart) {
        newDateInputs.start = comparisonStart;
        changed = true;
      }
      if (dateInputs.end !== comparisonEnd) {
        newDateInputs.end = comparisonEnd;
        changed = true;
      }
      if (changed) {
        return newDateInputs;
      }
      return dateInputs;
    });
  }, [comparisonRange, comparisonStart, comparisonEnd]);

  useEffect(() => {
    setDateInputs(dateInputs => {
      let newDateInputs = { ...dateInputs };
      let changed = false;
      if (dateInputs.start !== start) {
        newDateInputs.start = start;
        changed = true;
      }
      if (dateInputs.end !== end) {
        newDateInputs.end = end;
        changed = true;
      }
      if (changed) {
        return newDateInputs;
      }
      return dateInputs;
    });
  }, [start, end]);

  const onChangeInternal = useCallback(
    (dateRange: Partial<DateRange>, focus: typeof FOCUS_OPTIONS[number]) => {
      setDateInputs(dateRange);
      setFocus(focus);
      setSelectedPreset(null);
    },
    []
  );

  const onChangeComparisonInternal = useCallback(
    (dateRange: Partial<DateRange>, focus: typeof FOCUS_OPTIONS[number]) => {
      setComparisonDateInputs(dateRange);
      setComparisonFocus(focus);
      setSelectedCompPerType(null);
    },
    []
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShow(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [setShow]);

  const onDatePresetSelect = useCallback(
    preset => {
      try {
        const newRange = computeDateFromPreset(preset, fiscalCal);
        setDateInputs(newRange ? newRange : { start: start, end: end });
      } catch {
        console.log("Error computing date from comparison preset");
      }
    },
    [fiscalCal, start, end]
  );

  useEffect(() => {
    if (
      dateInputs.start &&
      dateInputs.end &&
      (dateInputs.start !== start || dateInputs.end !== end)
    ) {
      setFirstUserChange(f => (f[0] >= 2 ? f : [f[0] + 1, f[1]]));
    } else if (dateInputs.start === start && dateInputs.end === end) {
      setFirstUserChange(f => [0, f[1]]);
    }
  }, [dateInputs, start, end]);

  useEffect(() => {
    if (
      comparisonDateInputs.start &&
      comparisonDateInputs.end &&
      (comparisonDateInputs.start !== comparisonStart || comparisonDateInputs.end !== comparisonEnd)
    ) {
      setFirstUserChange(f => (f[1] >= 2 ? f : [f[0], f[1] + 1]));
    }
  }, [comparisonDateInputs, comparisonStart, comparisonEnd]);

  useEffect(() => {
    if (firstUserChange[0] === 1 && !firstUserChange[1]) {
      setSelectedCompPerType("Default");
    }
  }, [firstUserChange]);

  useEffect(() => {
    const { start, end } = dateInputs;
    if (!start || !end) {
      return;
    }

    if (selectedCompPerType !== null) {
      try {
        const newRange = computeDateFromComparisonPreset(
          selectedCompPerType,
          start,
          end,
          fiscalCal
        );
        if (newRange) {
          if (
            (newRange.start && resolvedIsOutsideValidRange(newRange.start)) ||
            (newRange.end && resolvedIsOutsideValidRange(newRange.end))
          ) {
            setSelectedCompPerType(null);
          } else {
            setComparisonDateInputs(newRange);
          }
        }
      } catch {
        console.log("Error computing date from comparison preset");
      }
    }
  }, [
    dateInputs,
    comparisonEnd,
    comparisonStart,
    selectedCompPerType,
    fiscalCal,
    resolvedIsOutsideValidRange,
  ]);

  return (
    <>
      <DatePickerButton
        reference={ref}
        setShow={setShow}
        range={range}
        fullWeeksOnly={fullWeeksOnly}
        setFocus={setFocus}
        comparison={comparison}
        comparisonRange={comparisonRange}
        light={light}
        disabled={disabled}
        type={type}
        design={design}
      ></DatePickerButton>
      {show &&
        ReactDOM.createPortal(
          <div className="DPDateContainer">
            <div
              className="overlay"
              onClick={() => setShow(false)}
              style={resolvedPosition.overlay}
            />
            <div className="calendarBox" style={resolvedPosition.position}>
              <div className="DPheader">
                <div className="headerTitleText">
                  {removeHeaderText ? "" : "Primary Date Range"}
                </div>
                <div className="rightHeaderItems">
                  {additionalRightHeaderItems}
                  <div className="dateDisplay">
                    <DateInputField
                      label="Start"
                      value={dateInputs.start || ""}
                      setValue={(value: string) => {
                        setDateInputs(d =>
                          !d.end || value > d.end ? { start: value } : { ...d, start: value }
                        );
                      }}
                      invalidateDateOrRange={resolvedIsOutsideValidRange}
                      clearPreset={() => setSelectedPreset(null)}
                    />
                    <DateInputField
                      label="End"
                      value={dateInputs.end || ""}
                      setValue={(value: string) => {
                        setDateInputs(d =>
                          !d.start || value < d.start ? { end: value } : { ...d, end: value }
                        );
                      }}
                      invalidateDateOrRange={resolvedIsOutsideValidRange}
                      clearPreset={() => setSelectedPreset(null)}
                    />
                  </div>
                </div>
              </div>
              <div className="calendarAndControls primary">
                <DPCalendar
                  key={"primaryDPCalendar"}
                  start={dateInputs.start}
                  end={dateInputs.end}
                  focus={focus}
                  onChange={onChangeInternal}
                  isOutsideRange={isOutsideRange}
                  isDayBlocked={isDayBlocked}
                  years={years}
                  currentMonthSecond={currentMonthSecond}
                  maxDate={maxDate}
                  fiscalCalOverride={fiscalCalOverride}
                />
                {(datePresets || dateInterval) && (
                  <div className="controls">
                    {datePresets && (
                      <div className="presetsContainer singleControlContainer">
                        <div className="controlSectionLabel">
                          <div className="sectionLabelWrapper">
                            Presets <span>(Optional)</span>
                          </div>
                        </div>
                        <DPPresets
                          setSelectedPreset={preset =>
                            setSelectedPreset(p => {
                              const setValue = p === preset ? null : preset;
                              onDatePresetSelect(setValue);
                              return setValue;
                            })
                          }
                          selectedPreset={selectedPreset}
                          setRangeErrorPreset={setRangeErrorPreset}
                          setDateInputs={setDateInputs}
                          resolvedIsOutsideValidRange={resolvedIsOutsideValidRange}
                          startCustomPreset={startCustomPreset}
                          setStartCustomPreset={setStartCustomPreset}
                          endCustomPreset={endCustomPreset}
                          setEndCustomPreset={setEndCustomPreset}
                          rangeErrorPreset={rangeErrorPreset}
                          fiscalCalOverride={fiscalCalOverride}
                        />
                      </div>
                    )}
                    {dateInterval && (
                      <div className="singleControlContainer">
                        <div className="controlSectionLabel">Graph Intervals</div>
                        {DATE_INTERVALS.map((interval, i) => (
                          <Button
                            key={`DPIntervalOptionButton${i}`}
                            type={ButtonType.EMPTY}
                            design="secondary"
                            size="sm"
                            onClick={() => {
                              setSelectedInterval(i => (i === interval ? undefined : interval));
                              // TODO: make enable this feature
                              // dateIntervalOnChage(interval);
                            }}
                            className={cn("controlButton", {
                              selected: selectedInterval === interval,
                            })}
                          >
                            {interval}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {comparison && comparisonDateInputs && (
                <>
                  <div
                    className={`comHeaderWrapper top ${compOpen ? "compOpen" : "compClosed"}`}
                  ></div>
                  <div
                    className={`DPheader comparisonHeader ${
                      comparisonEnabled ? "" : "compHeadDisabled"
                    }`}
                  >
                    <div className="headerTitleText">
                      {"Comparison Date Range"}
                      {disableComparisonSetter && (
                        <ToggleSwitch
                          label=""
                          onChange={() => setComparisonEnabled(r => !r)}
                          checked={comparisonEnabled}
                          size="lg"
                        />
                      )}
                    </div>
                    <div className="rightHeaderItems">
                      <div
                        className={`dateDisplay comparison ${
                          comparisonEnabled ? "" : "comparisonDisabled"
                        }`}
                        onClick={() => setCompOpen(true)}
                      >
                        <DateInputField
                          label="Comparison Start"
                          value={comparisonDateInputs.start || ""}
                          setValue={(value: string) => {
                            setComparisonDateInputs(d =>
                              !d.end || value < d.end ? { ...d, start: value } : { start: value }
                            );
                          }}
                          invalidateDateOrRange={resolvedIsOutsideValidRange}
                          dashBoarder={true}
                          clearPreset={() => setSelectedCompPerType(null)}
                        />
                        <DateInputField
                          label="Comparison End"
                          value={comparisonDateInputs.end || ""}
                          setValue={(value: string) => {
                            setComparisonDateInputs(d =>
                              !d.start || value > d.start ? { ...d, end: value } : { end: value }
                            );
                          }}
                          invalidateDateOrRange={resolvedIsOutsideValidRange}
                          dashBoarder={true}
                          clearPreset={() => setSelectedCompPerType(null)}
                        />
                      </div>
                      <div
                        className={`DParrow ${comparisonEnabled ? "" : "compDisabledArrow"} ${
                          compOpen ? "" : "comparisonArrowClosed"
                        }`}
                        onClick={() => {
                          if (comparisonEnabled) {
                            setCompOpen(c => !c);
                          }
                        }}
                      >
                        <MdArrowForwardIos />
                      </div>
                    </div>
                  </div>
                  <div
                    className={`comHeaderWrapper ${compOpen ? "" : "compClosed"} ${
                      comparisonEnabled ? "" : "comparisonDisabled"
                    }`}
                  ></div>
                  <div
                    className={`calendarAndControls comparison ${
                      comparisonEnabled ? "" : "comparisonDisabled"
                    } ${compOpen ? "" : "comparisonClosed"}`}
                  >
                    <DPCalendar
                      key={"comparisonDPCalendar"}
                      start={comparisonDateInputs.start}
                      end={comparisonDateInputs.end}
                      focus={comparisonFocus}
                      onChange={onChangeComparisonInternal}
                      isOutsideRange={isOutsideRange}
                      isDayBlocked={isDayBlocked}
                      years={years}
                      currentMonthSecond={currentMonthSecond}
                      maxDate={maxDate}
                      fiscalCalOverride={fiscalCalOverride}
                      comparison={true}
                    />
                    {(datePresets || dateInterval) && (
                      <div className="controls">
                        {datePresets && (
                          <div className="presetsContainer singleControlContainer">
                            <div className="controlSectionLabel">
                              <div className="sectionLabelWrapper">
                                Presets <span>(Optional)</span>
                              </div>
                            </div>
                            <DPPresets
                              setSelectedPreset={preset =>
                                setSelectedCompPerType(p => {
                                  let setValue = preset;
                                  if (p === preset) {
                                    // when we "unpress it" go back to original input dates
                                    setValue = null;
                                    setComparisonDateInputs({
                                      start: comparisonStart,
                                      end: comparisonEnd,
                                    });
                                  }
                                  return setValue;
                                })
                              }
                              selectedPreset={selectedCompPerType}
                              setRangeErrorPreset={setCompRangeErrorPreset}
                              setDateInputs={setComparisonDateInputs}
                              resolvedIsOutsideValidRange={resolvedIsOutsideValidRange}
                              startCustomPreset={startCompCustomPreset}
                              setStartCustomPreset={setStartCompCustomPreset}
                              endCustomPreset={endCompCustomPreset}
                              setEndCustomPreset={setEndCompCustomPreset}
                              rangeErrorPreset={compRangeErrorPreset}
                              fiscalCalOverride={fiscalCalOverride}
                              comparison={true}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="DPfooter">
                {!R.isEmpty(cid) && R.isNil(fiscalCalOverride) && (
                  <OverlayTrigger
                    placement={OverlayTrigger.PLACEMENTS.LEFT.CENTER}
                    overlay={<Tooltip id="datePickerCalSettingstt">Calendar settings</Tooltip>}
                  >
                    <div
                      className="wrenchToCalendarButton"
                      onClick={() => {
                        window.location.href = `${window.location.origin}/${cid}/calendar-input`;
                      }}
                    >
                      <FaWrench className="wrenchIcon" />
                    </div>
                  </OverlayTrigger>
                )}
                <Button
                  type={ButtonType.OUTLINED}
                  design="secondary"
                  onClick={() => setShow(false)}
                >
                  Cancel
                </Button>
                <Button
                  type={ButtonType.FILLED}
                  onClick={() => {
                    setShow(false, true);
                    onApply && onApply();
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
